import { IBrowserStorage } from './types/browser-storage.interface';
import { StorageCorrespondingKeys } from './types/storage-corresponding-keys';
import { StorageType } from './types/storage-type';

export abstract class AbstractStorage<TStorageType extends StorageType> implements IBrowserStorage<unknown, StorageCorrespondingKeys<StorageType>> {
    private storage: Storage;

    protected constructor(storage: Storage) {
        this.storage = storage;
    }

    /**
     * Get an item in the selected storage.
     */
    getItem<T>(key: StorageCorrespondingKeys<TStorageType>): T {
        return JSON.parse(this.storage.getItem(key));
    }

    /**
     * Create a new item in the selected storage.
     * Use this method with app initialization to keep models in your storage actual.
     */
    setItem<T>(key: StorageCorrespondingKeys<TStorageType>, value: T): void {
        let existingData = this.getItem(key);
        existingData = existingData ? Object.assign(value, existingData) : value;

        this.storage.setItem(key, JSON.stringify(existingData));
    }

    /**
     * Replace an existing storage item with a new one, with no additional checks and actualization.
     * Use this for array-type items.
     */
    replaceItem<T>(key: StorageCorrespondingKeys<TStorageType>, newValue: T): void {
        this.storage.setItem(key, JSON.stringify(newValue));
    }

    /**
     * Update an item in the selected storage.
     * If an item doesn't exist, means that you forgot to init your key with model. In that case you will get an exception.
     */
    updateItem<T>(key: StorageCorrespondingKeys<TStorageType>, newValue: Partial<T>): void {
        const existing = this.getItem(key);
        if (!existing) throw new Error(`The selected storage doesn't have a key ${key}!`);

        const updated = Object.assign(existing, newValue);
        this.storage.setItem(key, JSON.stringify(updated));
    }

    /**
     * Remove an item in the selected storage.
     */
    removeItem(key: StorageCorrespondingKeys<TStorageType>, prop: string): void {
        const clearedProp = {};
        clearedProp[prop] = null;
        this.updateItem(key, clearedProp);
    }

    /**
     * Completely clear the selected storage.
     * Use carefully!
     */
    clear(): void {
        this.storage.clear();
    }

    /**
     * Check if an item exists in the selected storage.
     */
    existsItem(key: StorageCorrespondingKeys<TStorageType>): boolean {
        return !!this.getItem(key);
    }
}
